import type { RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

import { PlatformRights } from '@/clients/v1';
import { i18n } from '@/config/i18n.ts';
import { hasPlatformRights } from '@/composables/Auth.ts';
import { useAIUsageMetricsStore } from '@/stores/v2/AIUsageMetricsStore.ts';

import DefaultLayout from '@/layouts/DefaultLayout.vue';
import { BreadcrumbFactory } from '@/router/utils/breadcrumbFactory.ts';

export const adminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin',
    name: 'admin',
    meta: { layout: DefaultLayout, breadcrumb: 'Admin', sidebarSection: 'admin' },
    component: () => import('@/pages/Admin/AdminPage.vue'),
    beforeEnter: async () => {
      // Anyone with a platform-management baseline (read on platform orgs)
      // can reach /admin. Sub-pages have stricter gates.
      if (!hasPlatformRights(PlatformRights.PlatformOrganizationsRead)) {
        return { name: 'home' };
      }
    },
    children: [
      {
        path: '',
        redirect: () =>
          hasPlatformRights(PlatformRights.PlatformOrganizationsMetricsRead)
            ? { name: 'admin-ai-usage' }
            : { name: 'admin-organizations' },
      },
      {
        path: 'ai-usage',
        meta: {
          breadcrumb: () => ({ label: i18n.global.t('AI Usage'), to: '/admin/ai-usage' }),
        },
        beforeEnter: () => {
          if (!hasPlatformRights(PlatformRights.PlatformOrganizationsMetricsRead)) {
            return { name: 'admin-organizations' };
          }
        },
        children: [
          {
            path: '',
            name: 'admin-ai-usage',
            component: () => import('@/pages/Admin/pages/AIUsage/AIUsagePage.vue'),
          },
          {
            path: 'events',
            name: 'admin-ai-usage-events',
            component: () => import('@/pages/Admin/pages/AIUsage/AIUsageEventsPage.vue'),
            meta: { breadcrumb: () => ({ label: i18n.global.t('AI usage events') }) },
          },
          {
            path: 'organizations/:organizationId',
            name: 'admin-ai-usage-organization-detail',
            component: () => import('@/pages/Admin/pages/AIUsage/OrganizationUsagePage.vue'),
            meta: {
              breadcrumb: BreadcrumbFactory.aiUsageOrganization(() => useAIUsageMetricsStore()),
            },
          },
        ],
      },
      {
        path: 'ai-pricing',
        name: 'admin-ai-pricing',
        component: () => import('@/pages/Admin/pages/AIPricing/AIPricingPage.vue'),
        meta: {
          breadcrumb: () => ({ label: i18n.global.t('AI Pricing'), to: '/admin/ai-pricing' }),
        },
        beforeEnter: () => {
          if (!hasPlatformRights(PlatformRights.PlatformOrganizationsMetricsRead)) {
            return { name: 'admin-organizations' };
          }
        },
      },
      {
        path: 'organizations',
        name: 'admin-organizations',
        component: () => import('@/pages/Admin/OrganizationsPage.vue'),
        meta: {
          breadcrumb: 'Organizations',
        },
      },
      {
        path: 'frameworks',
        name: 'admin-frameworks',
        component: () => import('@/pages/Admin/pages/Frameworks/FrameworksPage.vue'),
        meta: {
          breadcrumb: 'Frameworks',
        },
        beforeEnter: () => {
          if (!hasPlatformRights(PlatformRights.PlatformFrameworksRead)) {
            return { name: 'admin-organizations' };
          }
        },
        children: [
          {
            path: '',
            name: 'admin-frameworks-overview',
            component: () => import('@/pages/Admin/pages/Frameworks/FrameworksOverview.vue'),
          },
          {
            path: 'new-template',
            name: 'admin-new-template',
            component: () => import('@/pages/Admin/pages/Frameworks/NewTemplate.vue'),
            meta: {
              breadcrumb: 'New Template',
            },
            beforeEnter: () => {
              if (!hasPlatformRights(PlatformRights.PlatformFrameworksCreate)) {
                return { name: 'admin-frameworks-overview' };
              }
            },
          },
          {
            path: ':frameworkCode',
            name: 'admin-framework-detail',
            component: () => import('@/pages/Admin/pages/Frameworks/FrameworkDetail.vue'),
            meta: {
              breadcrumb: (route: RouteLocationNormalized) => `${route.params.frameworkCode}`,
            },
          },
        ],
      },
      {
        path: 'templates',
        name: 'admin-templates',
        component: () => import('@/pages/Admin/pages/Templates/TemplatesPage.vue'),
        meta: {
          breadcrumb: 'Policy Templates',
        },
        beforeEnter: () => {
          if (!hasPlatformRights(PlatformRights.PlatformPolicyTemplateRead)) {
            return { name: 'admin-organizations' };
          }
        },
        children: [
          {
            path: '',
            name: 'admin-templates-overview',
            component: () => import('@/pages/Admin/pages/Templates/TemplatesOverview.vue'),
          },
          {
            path: ':templateId',
            name: 'admin-template-detail',
            component: () => import('@/pages/Admin/pages/Templates/TemplateDetailPage.vue'),
            meta: {
              breadcrumb: 'Policy Template',
            },
          },
          {
            path: ':templateId/versions/:versionId',
            name: 'admin-template-version-detail',
            component: () => import('@/pages/Admin/pages/Templates/TemplateVersionDetailPage.vue'),
            meta: {
              breadcrumb: 'Version',
            },
          },
        ],
      },
    ],
  },
];
